import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { GetPropertyListResponseModel, ISoilProfilePlaceGeoNames } from '../../util/Request/requestType';
import { ICoordinate } from '@/components/GoogleMapReact/GoogleMapReactComponent';

interface IRadiusSetting {
    defaultValue: number
    maxValue: number
}

interface ISearchModel {
    // radius setting
    radiusSetting?: IRadiusSetting,
    fromQueryHistory?: boolean,
    // use for dropdown list
    soilPropertyList?: GetPropertyListResponseModel[]
    firstIn?: boolean
    soilQueryId?: number
    queryGuid?: string
    // soil query common property
    isMapView?: boolean
    // first tab
    selectedPropertySet?: GetPropertyListResponseModel[]
    currentRadius?: number
    placeSelectSet?: ISoilProfilePlaceGeoNames[]
    // second tab
    coordinatesSet?: string[]
    coordinateTabRadius?: number
    coordinateTabPropertiesSet?: GetPropertyListResponseModel[]
    // third tab
    areaIds?: string[]
    adminiTabPropertySet?: GetPropertyListResponseModel[]
    checkedKeys?: string[]
    checkedNames?: string[]
    expandedKeys?: string[]
    // fourth tab
    polyPoint?: ICoordinate[]
    circlePoint?: ICoordinate[]
    center?: ICoordinate
    radius?: number
    drawPolyTabPropertiesSet?: GetPropertyListResponseModel[]
    drawReactModel?: boolean
    drawCircleMode?: boolean
}

const initialState: ISearchModel = {
    radiusSetting: {
        defaultValue: 1,
        maxValue: 10
    },
    soilPropertyList: [],
    firstIn: true,
    soilQueryId: 0,
    currentRadius: 1,
    coordinateTabRadius: 1,
    selectedPropertySet: [],
    placeSelectSet: [],
    coordinatesSet: [],
    coordinateTabPropertiesSet: [],
    areaIds: [],
    adminiTabPropertySet: [],
    polyPoint: [],
    drawPolyTabPropertiesSet: [],
    drawReactModel: false,
    drawCircleMode: false,
    isMapView: true,
    checkedKeys: [],
    checkedNames: [],
    expandedKeys: []
};

export const soilSearchSlice = createSlice({
    name: 'search',
    initialState,
    reducers: {
        updateSearchRadiusSetting: (state, action: PayloadAction<IRadiusSetting>) => {
            return {
                ...state,
                radiusSetting: action.payload
            };
        },
        updatePropertyList: (state, action: PayloadAction<GetPropertyListResponseModel[]>) => {
            return {
                ...state,
                soilPropertyList: action.payload
            };
        },
        updateFirstIn: (state, action: PayloadAction<boolean>) => {
            return {
                ...state,
                firstIn: action.payload
            };
        },
        updateSoilQueryId: (state, action: PayloadAction<number>) => {
            return {
                ...state,
                soilQueryId: action.payload
            };
        },
        updateSoilQueryGuid: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                queryGuid: action.payload,
            };
        },
        updateFromQueryHistory: (state, action: PayloadAction<boolean>) => {
            return {
                ...state,
                fromQueryHistory: action.payload,
            };
        },
        updateFirstTabValues: (state, action: PayloadAction<{
            selectedPropertySet?: GetPropertyListResponseModel[]
            currentRadius?: number
            placeSelectSet?: ISoilProfilePlaceGeoNames[]
            isMapView?: boolean
            fromQueryHistory?: boolean
        }>) => {
            return {
                ...state,
                ...action.payload
            };
        },
        updateSecondTabValues: (state: ISearchModel, action: PayloadAction<{
            coordinatesSet?: string[]
            coordinateTabRadius?: number
            coordinateTabPropertiesSet?: GetPropertyListResponseModel[]
            isMapView?: boolean
            fromQueryHistory?: boolean
        }>) => {
            return {
                ...state,
                ...action.payload
            };
        },
        updateThirdTabValues: (state: ISearchModel, action: PayloadAction<{
            areaIds?: string[]
            adminiTabPropertySet?: GetPropertyListResponseModel[]
            isMapView?: boolean
            checkedKeys?: string[]
            checkedNames?: string[]
            expandedKeys?: string[]
            fromQueryHistory?: boolean
        }>) => {
            return {
                ...state,
                ...action.payload
            };
        },
        updateFourthTabValues: (state: ISearchModel, action: PayloadAction<{
            polyPoint?: ICoordinate[]
            drawPolyTabPropertiesSet?: GetPropertyListResponseModel[]
            isMapView?: boolean
            circlePoint?: ICoordinate[]
            center?: ICoordinate
            radius?: number
            fromQueryHistory?: boolean
        }>) => {
            return {
                ...state,
                ...action.payload
            };
        },
        updateFourthDrawFlag: (state: ISearchModel, action: PayloadAction<{
            drawCircleMode?: boolean
            drawReactModel?: boolean
        }>) => {
            return {
                ...state,
                ...action.payload
            };
        },
    }
});

export type {
    ISearchModel
};

export const {
    updateSearchRadiusSetting,
    updatePropertyList,
    updateFirstIn,
    updateSoilQueryId,
    updateSoilQueryGuid,
    updateFirstTabValues,
    updateSecondTabValues,
    updateThirdTabValues,
    updateFourthTabValues,
    updateFromQueryHistory,
    updateFourthDrawFlag
} = soilSearchSlice.actions;

export const selectSearchSoilSearch = (state: RootState) => state.soilSearchPageData;

export default soilSearchSlice.reducer;